import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import { api } from '../../lib/api';
import { LoadingBlock } from '../../components/Spinner';
import type { Venue, EventSeries } from '../../types';

/**
 * Operations → Scan cadence page (`/ops/scan-cadence`).
 *
 * Single dashboard for understanding and tuning the auto-scan engine for
 * BOTH venues and series. They share the same staleness model, the same
 * favorited × stale 2x2 cooldown matrix (with separate venue/series
 * cooldown values), and the same per-channel scan tracking.
 */

type Channel = 'instagram' | 'facebook' | 'website';
type TargetKind = 'venue' | 'series';

interface LifetimeRow {
  targetKind: TargetKind | 'source';
  targetId: string;
  targetName: string;
  channel: Channel | string;
  totalScans: number;
  totalPostsSeen: number;
  totalPostsNew: number;
  /** New: total accepted events derived from this (target, channel). */
  totalEventsAccepted?: number;
  errorScans: number;
  newRatio: number;
  /** New: accepted-events / posts-seen ratio. */
  acceptRatio?: number;
  avgPostsPerScan: number;
  firstScannedAt: string;
  lastScannedAt: string;
  isStale: boolean;
}

interface LifetimeResponse {
  retentionDays: number;
  totalRows: number;
  items: LifetimeRow[];
}

interface CadenceSettings {
  venueScanCooldownDays?: number;
  favoriteVenueScanCooldownDays?: number;
  staleVenueScanCooldownDays?: number;
  staleFavoriteVenueScanCooldownDays?: number;
  seriesScanCooldownDays?: number;
  favoriteSeriesScanCooldownDays?: number;
  staleSeriesScanCooldownDays?: number;
  staleFavoriteSeriesScanCooldownDays?: number;
  redditScanCooldownDays?: number;
  staleScanLookbackCount?: number;
  staleScanMaxNewPosts?: number;
  instagramStaleAfterEmptyScans?: number;
  instagramStaleRecheckDays?: number;
  instagramScanResultsLimit?: number;
  favoriteInstagramScanResultsLimit?: number;
  scanHistoryRetentionDays?: number;
  facebookScanNonFavoritedEnabled?: boolean;
  useSociavaultForInstagram?: boolean;
  useSociavaultForReddit?: boolean;
  redditPollEnabled?: boolean;
  // Per-channel rate-limit jitter (ms). Each scan sleeps a random duration
  // in [min, max] between channel calls to spread load + avoid bans.
  instagramScanDelayMinMs?: number;
  instagramScanDelayMaxMs?: number;
  facebookScanDelayMinMs?: number;
  facebookScanDelayMaxMs?: number;
  websiteScanDelayMinMs?: number;
  websiteScanDelayMaxMs?: number;
  // Per-channel cooldown overrides (optional). When undefined the
  // type-level cooldown above is used.
  venueInstagramScanCooldownDays?: number;
  favoriteVenueInstagramScanCooldownDays?: number;
  staleVenueInstagramScanCooldownDays?: number;
  staleFavoriteVenueInstagramScanCooldownDays?: number;
  venueFacebookScanCooldownDays?: number;
  favoriteVenueFacebookScanCooldownDays?: number;
  staleVenueFacebookScanCooldownDays?: number;
  staleFavoriteVenueFacebookScanCooldownDays?: number;
  venueWebsiteScanCooldownDays?: number;
  favoriteVenueWebsiteScanCooldownDays?: number;
  staleVenueWebsiteScanCooldownDays?: number;
  staleFavoriteVenueWebsiteScanCooldownDays?: number;
  seriesInstagramScanCooldownDays?: number;
  favoriteSeriesInstagramScanCooldownDays?: number;
  staleSeriesInstagramScanCooldownDays?: number;
  staleFavoriteSeriesInstagramScanCooldownDays?: number;
  seriesFacebookScanCooldownDays?: number;
  favoriteSeriesFacebookScanCooldownDays?: number;
  staleSeriesFacebookScanCooldownDays?: number;
  staleFavoriteSeriesFacebookScanCooldownDays?: number;
  seriesWebsiteScanCooldownDays?: number;
  favoriteSeriesWebsiteScanCooldownDays?: number;
  staleSeriesWebsiteScanCooldownDays?: number;
  staleFavoriteSeriesWebsiteScanCooldownDays?: number;
}

interface ChannelStat {
  lastScanAt?: string;
  nextEligibleAt?: string;
  lastError?: string;
  lifetime?: LifetimeRow;
  configured: boolean;
}

interface TargetRow {
  kind: TargetKind;
  id: string;
  name: string;
  partitionKey: string;
  active: boolean;
  detailUrl: string;
  channels: Record<Channel, ChannelStat>;
  staleChannelCount: number;
  totalEventsAccepted: number;
  totalPostsNew: number;
  oldestScanMs: number;
  configuredChannelCount: number;
}

type StalenessFilter = 'any' | 'any-stale' | 'all-stale' | 'all-active' | 'never-scanned';
type ChannelFilter = 'any' | 'instagram' | 'facebook' | 'website' | 'missing-any';
type KindFilter = 'all' | 'venue' | 'series';

interface Filters {
  search: string;
  partition: string;
  staleness: StalenessFilter;
  channel: ChannelFilter;
  kind: KindFilter;
  activeOnly: boolean;
}

const DEFAULT_FILTERS: Filters = {
  search: '',
  partition: '',
  staleness: 'any',
  channel: 'any',
  kind: 'all',
  activeOnly: true,
};

type SortKey = 'name' | 'partition' | 'kind' | 'oldestScan' | 'staleCount' | 'accepted' | 'totalNew';
type SortDir = 'asc' | 'desc';

function relativeTime(iso: string | undefined): string {
  if (!iso) return 'never';
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return 'never';
  const diffMs = Date.now() - t;
  const sec = Math.round(diffMs / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 30) return `${day}d ago`;
  const mo = Math.round(day / 30);
  return `${mo}mo ago`;
}

function nextEligibleLabel(iso: string | undefined): string {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '—';
  const diffMs = t - Date.now();
  if (diffMs <= 0) return 'eligible now';
  const min = Math.round(diffMs / 60_000);
  if (min < 60) return `in ${min}m`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `in ${hr}h`;
  const day = Math.round(hr / 24);
  return `in ${day}d`;
}

function summarise(
  base: Omit<TargetRow, 'staleChannelCount' | 'totalEventsAccepted' | 'totalPostsNew' | 'oldestScanMs' | 'configuredChannelCount'>
): TargetRow {
  let staleChannelCount = 0;
  let totalEventsAccepted = 0;
  let totalPostsNew = 0;
  let oldestScanMs = Number.POSITIVE_INFINITY;
  let configuredChannelCount = 0;
  for (const c of Object.values(base.channels)) {
    if (!c.configured) continue;
    configuredChannelCount += 1;
    if (c.lifetime?.isStale) staleChannelCount += 1;
    totalEventsAccepted += c.lifetime?.totalEventsAccepted ?? 0;
    totalPostsNew += c.lifetime?.totalPostsNew ?? 0;
    if (c.lastScanAt) {
      const t = new Date(c.lastScanAt).getTime();
      if (Number.isFinite(t) && t < oldestScanMs) oldestScanMs = t;
    } else {
      oldestScanMs = 0;
    }
  }
  if (oldestScanMs === Number.POSITIVE_INFINITY) oldestScanMs = 0;
  return { ...base, staleChannelCount, totalEventsAccepted, totalPostsNew, oldestScanMs, configuredChannelCount };
}

function buildVenueRow(v: Venue, lifetimeByKey: Map<string, LifetimeRow>): TargetRow {
  const fbLifetime = lifetimeByKey.get(`venue:${v.rowKey}:facebook`);
  const ig: ChannelStat = {
    configured: !!v.instagramHandle,
    lastScanAt: v.lastSocialScanAt,
    nextEligibleAt: v.nextScanEligibleAt,
    lastError: v.lastSocialScanError,
    lifetime: lifetimeByKey.get(`venue:${v.rowKey}:instagram`),
  };
  const fb: ChannelStat = {
    configured: !!v.facebookUrl,
    // Venue type doesn't surface lastFacebookScan* fields; fall back to lifetime row.
    lastScanAt: fbLifetime?.lastScannedAt,
    lifetime: fbLifetime,
  };
  const web: ChannelStat = {
    configured: !!v.website,
    lastScanAt: v.lastWebsiteScanAt,
    nextEligibleAt: v.nextWebsiteScanEligibleAt,
    lastError: v.lastWebsiteScanError,
    lifetime: lifetimeByKey.get(`venue:${v.rowKey}:website`),
  };
  return summarise({
    kind: 'venue',
    id: v.rowKey,
    name: v.name,
    partitionKey: v.partitionKey || '',
    active: v.active !== false,
    detailUrl: `/venues/${v.rowKey}`,
    channels: { instagram: ig, facebook: fb, website: web },
  });
}

function buildSeriesRow(s: EventSeries, lifetimeByKey: Map<string, LifetimeRow>): TargetRow {
  const ig: ChannelStat = {
    configured: !!s.instagramHandle,
    lastScanAt: s.lastSocialScanAt,
    nextEligibleAt: s.nextScanEligibleAt,
    lastError: s.lastSocialScanError,
    lifetime: lifetimeByKey.get(`series:${s.rowKey}:instagram`),
  };
  const fb: ChannelStat = {
    configured: !!s.facebookUrl,
    lastScanAt: s.lastFacebookScanAt,
    nextEligibleAt: s.nextFacebookScanEligibleAt,
    lastError: s.lastFacebookScanError,
    lifetime: lifetimeByKey.get(`series:${s.rowKey}:facebook`),
  };
  const web: ChannelStat = {
    configured: !!s.website,
    lastScanAt: s.lastWebsiteScanAt,
    nextEligibleAt: s.nextWebsiteScanEligibleAt,
    lastError: s.lastWebsiteScanError,
    lifetime: lifetimeByKey.get(`series:${s.rowKey}:website`),
  };
  return summarise({
    kind: 'series',
    id: s.rowKey,
    name: s.name,
    partitionKey: s.partitionKey || '',
    // Only `status === 'active'` series are actually polled by the
    // timer-poll-series-* timers (rowFilter "status eq 'active'"). Candidates
    // sit in the parking lot until an admin promotes or dismisses them, so
    // treating them as "active" here made the page misleading — they would
    // show "eligible now" forever despite never being scanned.
    active: s.status === 'active',
    detailUrl: `/series/${s.rowKey}`,
    channels: { instagram: ig, facebook: fb, website: web },
  });
}

function applyFilters(rows: TargetRow[], f: Filters): TargetRow[] {
  const search = f.search.trim().toLowerCase();
  return rows.filter((r) => {
    if (f.activeOnly && !r.active) return false;
    if (f.kind !== 'all' && r.kind !== f.kind) return false;
    if (search && !r.name.toLowerCase().includes(search)) return false;
    if (f.partition && r.partitionKey !== f.partition) return false;
    if (f.channel !== 'any') {
      if (f.channel === 'missing-any') {
        if (r.configuredChannelCount === 3) return false;
      } else if (!r.channels[f.channel].configured) {
        return false;
      }
    }
    switch (f.staleness) {
      case 'any-stale':
        if (r.staleChannelCount === 0) return false;
        break;
      case 'all-stale':
        if (r.configuredChannelCount === 0 || r.staleChannelCount !== r.configuredChannelCount) return false;
        break;
      case 'all-active':
        if (r.staleChannelCount > 0) return false;
        break;
      case 'never-scanned':
        if (r.oldestScanMs !== 0) return false;
        break;
    }
    return true;
  });
}

function compareRows(a: TargetRow, b: TargetRow, key: SortKey): number {
  switch (key) {
    case 'name':
      return a.name.localeCompare(b.name);
    case 'partition':
      return a.partitionKey.localeCompare(b.partitionKey);
    case 'kind':
      return a.kind.localeCompare(b.kind);
    case 'oldestScan':
      return a.oldestScanMs - b.oldestScanMs;
    case 'staleCount':
      return a.staleChannelCount - b.staleChannelCount;
    case 'accepted':
      return a.totalEventsAccepted - b.totalEventsAccepted;
    case 'totalNew':
      return a.totalPostsNew - b.totalPostsNew;
  }
}

function ChannelCell({ stat, label }: { stat: ChannelStat; label: string }) {
  if (!stat.configured) {
    return (
      <div className="text-body-secondary small">
        <span aria-hidden>—</span>
        <span className="visually-hidden">No {label} configured</span>
      </div>
    );
  }
  const stale = stat.lifetime?.isStale === true;
  const accepted = stat.lifetime?.totalEventsAccepted ?? 0;
  const totalNew = stat.lifetime?.totalPostsNew ?? 0;
  const totalScans = stat.lifetime?.totalScans ?? 0;
  return (
    <div className="small">
      <div className="d-flex align-items-center gap-1 flex-wrap">
        {stale ? (
          <span className="badge bg-warning text-dark" title="Engine is using the longer 'stale' cooldown for this channel">
            stale
          </span>
        ) : stat.lifetime ? (
          <span className="badge bg-success-subtle text-success-emphasis" title="Engine is using the base cooldown">
            active
          </span>
        ) : (
          <span className="badge bg-secondary-subtle text-secondary-emphasis">never scanned</span>
        )}
        {stat.lastError ? (
          <span className="badge bg-danger" title={stat.lastError}>
            error
          </span>
        ) : null}
      </div>
      <div className="text-body-secondary mt-1">
        {stat.lastScanAt ? `Last: ${relativeTime(stat.lastScanAt)}` : 'Last: never'}
      </div>
      {stat.nextEligibleAt ? <div className="text-body-secondary">Next: {nextEligibleLabel(stat.nextEligibleAt)}</div> : null}
      {stat.lifetime ? (
        <div className="text-body-secondary">
          <strong>{accepted}</strong> accepted / {totalNew} new in {totalScans} scans
        </div>
      ) : null}
    </div>
  );
}

type NumericSettingKey =
  | 'venueScanCooldownDays'
  | 'favoriteVenueScanCooldownDays'
  | 'staleVenueScanCooldownDays'
  | 'staleFavoriteVenueScanCooldownDays'
  | 'seriesScanCooldownDays'
  | 'favoriteSeriesScanCooldownDays'
  | 'staleSeriesScanCooldownDays'
  | 'staleFavoriteSeriesScanCooldownDays'
  | 'staleScanLookbackCount'
  | 'staleScanMaxNewPosts'
  | 'instagramStaleAfterEmptyScans'
  | 'instagramStaleRecheckDays'
  | 'instagramScanResultsLimit'
  | 'favoriteInstagramScanResultsLimit'
  | 'scanHistoryRetentionDays'
  | 'redditScanCooldownDays'
  | 'instagramScanDelayMinMs'
  | 'instagramScanDelayMaxMs'
  | 'facebookScanDelayMinMs'
  | 'facebookScanDelayMaxMs'
  | 'websiteScanDelayMinMs'
  | 'websiteScanDelayMaxMs'
  | 'venueInstagramScanCooldownDays'
  | 'favoriteVenueInstagramScanCooldownDays'
  | 'staleVenueInstagramScanCooldownDays'
  | 'staleFavoriteVenueInstagramScanCooldownDays'
  | 'venueFacebookScanCooldownDays'
  | 'favoriteVenueFacebookScanCooldownDays'
  | 'staleVenueFacebookScanCooldownDays'
  | 'staleFavoriteVenueFacebookScanCooldownDays'
  | 'venueWebsiteScanCooldownDays'
  | 'favoriteVenueWebsiteScanCooldownDays'
  | 'staleVenueWebsiteScanCooldownDays'
  | 'staleFavoriteVenueWebsiteScanCooldownDays'
  | 'seriesInstagramScanCooldownDays'
  | 'favoriteSeriesInstagramScanCooldownDays'
  | 'staleSeriesInstagramScanCooldownDays'
  | 'staleFavoriteSeriesInstagramScanCooldownDays'
  | 'seriesFacebookScanCooldownDays'
  | 'favoriteSeriesFacebookScanCooldownDays'
  | 'staleSeriesFacebookScanCooldownDays'
  | 'staleFavoriteSeriesFacebookScanCooldownDays'
  | 'seriesWebsiteScanCooldownDays'
  | 'favoriteSeriesWebsiteScanCooldownDays'
  | 'staleSeriesWebsiteScanCooldownDays'
  | 'staleFavoriteSeriesWebsiteScanCooldownDays';

interface SettingFieldDef {
  key: NumericSettingKey;
  label: string;
  hint: string;
  min: number;
  max: number;
}

const VENUE_FIELDS: SettingFieldDef[] = [
  { key: 'venueScanCooldownDays', label: 'Active', hint: 'Wait between scans for a venue producing accepted events.', min: 1, max: 365 },
  { key: 'favoriteVenueScanCooldownDays', label: 'Favorited × active', hint: 'Wait when any user has favorited the venue.', min: 1, max: 365 },
  { key: 'staleVenueScanCooldownDays', label: 'Stale', hint: 'Wait when recent scans produced few/no accepted events.', min: 1, max: 365 },
  { key: 'staleFavoriteVenueScanCooldownDays', label: 'Favorited × stale', hint: 'Wait for favorited venues that have gone quiet. Always shorter than non-favorited stale.', min: 1, max: 365 },
];

const SERIES_FIELDS: SettingFieldDef[] = [
  { key: 'seriesScanCooldownDays', label: 'Active', hint: 'Wait between scans for a series producing accepted events.', min: 1, max: 365 },
  { key: 'favoriteSeriesScanCooldownDays', label: 'Favorited × active', hint: 'Wait when any user has favorited the series.', min: 1, max: 365 },
  { key: 'staleSeriesScanCooldownDays', label: 'Stale', hint: 'Wait when recent scans produced few/no accepted events.', min: 1, max: 365 },
  { key: 'staleFavoriteSeriesScanCooldownDays', label: 'Favorited × stale', hint: 'Wait for favorited series that have gone quiet.', min: 1, max: 365 },
];

const SHARED_FIELDS: SettingFieldDef[] = [
  { key: 'instagramScanResultsLimit', label: 'Instagram posts per scan (default)', hint: 'How many recent posts to fetch per Instagram scan for non-favorited venues/series. Minimum 12 matches SociaVault’s default profile response size. Higher = more coverage. (SociaVault ~$0.0048/post; Apify is on the free $5/mo plan, so $0.)', min: 12, max: 30 },
  { key: 'favoriteInstagramScanResultsLimit', label: 'Instagram posts per scan (favorited)', hint: 'Override used for favorited venues/series. Minimum 12 matches SociaVault’s default profile response size; higher values fetch additional pages.', min: 12, max: 30 },
  { key: 'instagramStaleAfterEmptyScans', label: 'IG stale after N empty scans', hint: 'Stop scanning an Instagram handle on the normal cadence after this many CONSECUTIVE scans return zero posts (dead / renamed / private handles). A single scan that returns posts resets the count. Default 3.', min: 1, max: 20 },
  { key: 'instagramStaleRecheckDays', label: 'IG stale re-check (days)', hint: 'How often to re-check an Instagram handle that has been marked stale by the setting above. It is scanned once on this cadence; if it returns posts it resumes the normal cadence, otherwise it waits another cycle. Default 30 (~monthly).', min: 1, max: 365 },
  { key: 'staleScanLookbackCount', label: 'Stale lookback (recent scans)', hint: 'How many recent scan history rows to inspect when deciding whether a channel is stale.', min: 2, max: 50 },
  { key: 'staleScanMaxNewPosts', label: 'Stale threshold (max accepted events)', hint: 'A channel is stale when the SUM of accepted events across the lookback window is at most this number.', min: 0, max: 50 },
  { key: 'scanHistoryRetentionDays', label: 'Scan history retention (days)', hint: 'How long to keep ScanHistory rows. Affects the lifetime aggregates shown below.', min: 30, max: 1825 },
];

/** Per-channel override field groups. Each group has the same 4 slots
 *  as the type-level Venue/Series cards. Leave any field blank to
 *  inherit the type-level cooldown above. */
type ChannelKind = 'venue' | 'series';
type ChannelName = 'instagram' | 'facebook' | 'website';

interface ChannelOverrideSlot {
  /** Override key on CadenceSettings. */
  key: NumericSettingKey;
  /** Type-level fallback key (used to show the inherited placeholder). */
  fallback: NumericSettingKey;
  label: string;
}

function overrideGroup(kind: ChannelKind, channel: ChannelName): ChannelOverrideSlot[] {
  const Kind = kind === 'venue' ? 'Venue' : 'Series';
  const Channel = channel === 'instagram' ? 'Instagram' : channel === 'facebook' ? 'Facebook' : 'Website';
  return [
    { key: `${kind}${Channel}ScanCooldownDays` as NumericSettingKey,                label: 'Active',             fallback: `${kind}ScanCooldownDays` as NumericSettingKey },
    { key: `favorite${Kind}${Channel}ScanCooldownDays` as NumericSettingKey,        label: 'Favorited × active', fallback: `favorite${Kind}ScanCooldownDays` as NumericSettingKey },
    { key: `stale${Kind}${Channel}ScanCooldownDays` as NumericSettingKey,           label: 'Stale',              fallback: `stale${Kind}ScanCooldownDays` as NumericSettingKey },
    { key: `staleFavorite${Kind}${Channel}ScanCooldownDays` as NumericSettingKey,   label: 'Favorited × stale',  fallback: `staleFavorite${Kind}ScanCooldownDays` as NumericSettingKey },
  ];
}

const CHANNEL_OVERRIDE_GROUPS: { kind: ChannelKind; channel: ChannelName; title: string; slots: ChannelOverrideSlot[] }[] = [
  { kind: 'venue',  channel: 'instagram', title: 'Venues · Instagram', slots: overrideGroup('venue', 'instagram') },
  { kind: 'venue',  channel: 'facebook',  title: 'Venues · Facebook',  slots: overrideGroup('venue', 'facebook') },
  { kind: 'venue',  channel: 'website',   title: 'Venues · Website',   slots: overrideGroup('venue', 'website') },
  { kind: 'series', channel: 'instagram', title: 'Series · Instagram', slots: overrideGroup('series', 'instagram') },
  { kind: 'series', channel: 'facebook',  title: 'Series · Facebook',  slots: overrideGroup('series', 'facebook') },
  { kind: 'series', channel: 'website',   title: 'Series · Website',   slots: overrideGroup('series', 'website') },
];

/** Flat list of all per-channel override keys — used by the dirty check
 *  and the save handler so blanked-out overrides can be sent as `null`
 *  (explicit clear, server-side). */
const CHANNEL_OVERRIDE_KEYS: NumericSettingKey[] = CHANNEL_OVERRIDE_GROUPS.flatMap((g) => g.slots.map((s) => s.key));

function NumberField({
  field,
  value,
  onChange,
  placeholder,
  hintOverride,
}: {
  field: SettingFieldDef;
  value: number | undefined;
  onChange: (v: number | undefined) => void;
  /** Placeholder text shown when the input is blank — used by per-channel
   *  override fields to surface the inherited type-level default. */
  placeholder?: string;
  /** Optional hint text override (e.g. for inherited-default rows). */
  hintOverride?: string;
}) {
  const id = `cadence-${String(field.key)}`;
  return (
    <div>
      <label htmlFor={id} className="form-label small mb-1">
        {field.label}
      </label>
      <input
        id={id}
        type="number"
        className="form-control form-control-sm"
        min={field.min}
        max={field.max}
        value={value ?? ''}
        placeholder={placeholder}
        onChange={(e) => {
          const v = e.target.value === '' ? undefined : Number(e.target.value);
          onChange(v);
        }}
        aria-describedby={`${id}-hint`}
      />
      <div id={`${id}-hint`} className="form-text small">
        {hintOverride ?? field.hint}
      </div>
    </div>
  );
}

function SettingsCard({
  settings,
  onChange,
  onSave,
  saving,
  saved,
  error,
}: {
  settings: CadenceSettings;
  onChange: (s: CadenceSettings) => void;
  onSave: () => void;
  saving: boolean;
  saved: boolean;
  error: string | null;
}) {
  return (
    <section aria-labelledby="cadence-settings-h" className="card mb-4">
      <div className="card-body">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
          <h2 id="cadence-settings-h" className="h5 mb-0">
            Cadence settings
          </h2>
          <div className="d-flex align-items-center gap-2">
            {saved ? (
              <span className="text-success small" role="status">
                Saved.
              </span>
            ) : null}
            {error ? (
              <span className="text-danger small" role="alert">
                {error}
              </span>
            ) : null}
            <button type="button" className="btn btn-primary btn-sm" onClick={onSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </div>
        <p className="text-body-secondary small mb-3">
          Each value is enforced server-side within the listed range. Changes take effect on the next scan tick.
        </p>

        <h3 className="h6 mt-2">Venue cooldowns (days)</h3>
        <div className="row g-3 mb-3">
          {VENUE_FIELDS.map((f) => (
            <div key={String(f.key)} className="col-md-6 col-xl-3">
              <NumberField field={f} value={settings[f.key]} onChange={(v) => onChange({ ...settings, [f.key]: v })} />
            </div>
          ))}
        </div>

        <h3 className="h6 mt-2">Series cooldowns (days)</h3>
        <div className="row g-3 mb-3">
          {SERIES_FIELDS.map((f) => (
            <div key={String(f.key)} className="col-md-6 col-xl-3">
              <NumberField field={f} value={settings[f.key]} onChange={(v) => onChange({ ...settings, [f.key]: v })} />
            </div>
          ))}
        </div>

        <h3 className="h6 mt-2">Shared (applies to venues and series)</h3>
        <div className="row g-3">
          {SHARED_FIELDS.map((f) => (
            <div key={String(f.key)} className="col-md-6 col-xl-4">
              <NumberField field={f} value={settings[f.key]} onChange={(v) => onChange({ ...settings, [f.key]: v })} />
            </div>
          ))}
        </div>

        <h3 className="h6 mt-4">Per-channel cooldown overrides (days)</h3>
        <p className="text-body-secondary small mb-3">
          Optional. Leave a field blank to inherit the matching type-level cooldown above. Set a value here to
          override that single (kind, channel, slot) combination &mdash; useful when one channel (e.g. Facebook)
          burns much more credit per scan than another. Reddit has its own cadence and kill switch elsewhere.
        </p>
        {CHANNEL_OVERRIDE_GROUPS.map((group) => (
          <details key={`${group.kind}-${group.channel}`} className="mb-3">
            <summary className="fw-semibold small">{group.title}</summary>
            <div className="row g-3 mt-1">
              {group.slots.map((slot) => {
                const inherited = settings[slot.fallback];
                const placeholder = typeof inherited === 'number' ? `inherits ${inherited}d` : 'inherits default';
                const field: SettingFieldDef = {
                  key: slot.key,
                  label: slot.label,
                  hint: `Blank → inherit the type-level value (currently ${typeof inherited === 'number' ? `${inherited}d` : 'default'}).`,
                  min: 1,
                  max: 365,
                };
                return (
                  <div key={String(slot.key)} className="col-md-6 col-xl-3">
                    <NumberField
                      field={field}
                      value={settings[slot.key]}
                      placeholder={placeholder}
                      onChange={(v) => onChange({ ...settings, [slot.key]: v })}
                    />
                  </div>
                );
              })}
            </div>
          </details>
        ))}

        <h3 className="h6 mt-4">Channel cost gates</h3>
        <div className="form-check mt-2">
          <input
            id="cadence-fb-nonfav"
            type="checkbox"
            className="form-check-input"
            checked={settings.facebookScanNonFavoritedEnabled === true}
            onChange={(e) =>
              onChange({ ...settings, facebookScanNonFavoritedEnabled: e.target.checked })
            }
            aria-describedby="cadence-fb-nonfav-hint"
          />
          <label htmlFor="cadence-fb-nonfav" className="form-check-label">
            Scan Facebook for non-favorited venues &amp; series
          </label>
          <div id="cadence-fb-nonfav-hint" className="form-text small">
            Off by default. <code>apify/facebook-events-scraper</code> is the lowest-yield actor we run. (Apify is on the free $5/mo plan, so there is no per-run charge, but FB scans still spend that limited monthly credit + scan time.) When unchecked, the auto-scan timer skips Facebook scans for any non-favorited target entirely. Favorited targets always scan, and manual admin scans are unaffected.
          </div>
        </div>

        <div className="form-check mt-3">
          <input
            id="cadence-sv-ig"
            type="checkbox"
            className="form-check-input"
            checked={settings.useSociavaultForInstagram === true}
            onChange={(e) =>
              onChange({ ...settings, useSociavaultForInstagram: e.target.checked })
            }
            aria-describedby="cadence-sv-ig-hint"
          />
          <label htmlFor="cadence-sv-ig" className="form-check-label">
            Use SociaVault for Instagram scans
          </label>
          <div id="cadence-sv-ig-hint" className="form-text small">
            Off by default. When on, Instagram scans use SociaVault (~$0.0048/call) instead of Apify. (Apify is on the free $5/mo plan, so it has no per-run charge either; SociaVault mainly buys more reliable resolution + higher limits.) Requires the <code>SOCIAVAULT_API_KEY</code> secret in Key Vault (sign up at <a href="https://sociavault.com/signup" target="_blank" rel="noopener noreferrer">sociavault.com</a> for 50 free credits, then $29 one-time for ~6000 more &mdash; credits never expire). If the key is missing while this is on, scans fall back to Apify (logged) so ingestion doesn't break silently.
          </div>
        </div>

        <div className="form-check mt-3">
          <input
            id="cadence-reddit-poll-enabled"
            type="checkbox"
            className="form-check-input"
            checked={settings.redditPollEnabled !== false}
            onChange={(e) =>
              onChange({ ...settings, redditPollEnabled: e.target.checked })
            }
            aria-describedby="cadence-reddit-poll-enabled-hint"
          />
          <label htmlFor="cadence-reddit-poll-enabled" className="form-check-label">
            Reddit polling enabled
          </label>
          <div id="cadence-reddit-poll-enabled-hint" className="form-text small">
            Master kill switch for the daily Reddit poll timer. On by default. When off, the scheduled timer fires but exits immediately without scanning any Reddit sources or calling Reddit/SociaVault/Apify. Per-source <em>Rerun</em> buttons in the Sources admin still work, so you can trigger one-off scans while auto-polling is paused. Useful when Reddit traffic is noisy/costly and you want to pause without disabling each source individually.
          </div>
        </div>

        <div className="form-check mt-3">
          <input
            id="cadence-sv-reddit"
            type="checkbox"
            className="form-check-input"
            checked={settings.useSociavaultForReddit === true}
            onChange={(e) =>
              onChange({ ...settings, useSociavaultForReddit: e.target.checked })
            }
            aria-describedby="cadence-sv-reddit-hint"
          />
          <label htmlFor="cadence-sv-reddit" className="form-check-label">
            Use SociaVault for Reddit polling
          </label>
          <div id="cadence-sv-reddit-hint" className="form-text small">
            Off by default. Reddit's official OAuth API is already free, so leave this off unless you can't configure <code>REDDIT_CLIENT_ID</code>/<code>REDDIT_CLIENT_SECRET</code> or want to consolidate providers. When on, SociaVault becomes the highest-priority Reddit backend, with OAuth and Apify as fallbacks.
          </div>
        </div>

        <h3 className="h6 mt-4">Reddit cadence</h3>
        <div className="row g-3">
          <div className="col-md-4">
            <NumberField
              field={{
                key: 'redditScanCooldownDays',
                label: 'Reddit cooldown (days)',
                hint: 'Minimum gap between auto-poll scans of any single Reddit source. Default 7.',
                min: 1,
                max: 365,
              }}
              value={settings.redditScanCooldownDays}
              placeholder="default 7"
              onChange={(v) => onChange({ ...settings, redditScanCooldownDays: v })}
            />
          </div>
        </div>

        <h3 className="h6 mt-4">Rate-limit jitter (ms)</h3>
        <p className="text-body-secondary small mb-3">
          Per-channel random delay between sequential scan calls during a single poll cycle. Each call sleeps a uniform-random duration in <code>[min, max]</code>. Higher = friendlier to upstream APIs and lower ban risk; lower = faster but more aggressive. Defaults are conservative.
        </p>
        <div className="row g-3">
          <div className="col-md-4">
            <NumberField
              field={{ key: 'instagramScanDelayMinMs', label: 'Instagram min (ms)', hint: 'Default 10 000', min: 1_000, max: 180_000 }}
              value={settings.instagramScanDelayMinMs}
              placeholder="default 10000"
              onChange={(v) => onChange({ ...settings, instagramScanDelayMinMs: v })}
            />
          </div>
          <div className="col-md-4">
            <NumberField
              field={{ key: 'instagramScanDelayMaxMs', label: 'Instagram max (ms)', hint: 'Default 90 000', min: 5_000, max: 300_000 }}
              value={settings.instagramScanDelayMaxMs}
              placeholder="default 90000"
              onChange={(v) => onChange({ ...settings, instagramScanDelayMaxMs: v })}
            />
          </div>
          <div className="col-md-4"></div>
          <div className="col-md-4">
            <NumberField
              field={{ key: 'facebookScanDelayMinMs', label: 'Facebook min (ms)', hint: 'Default 10 000', min: 1_000, max: 180_000 }}
              value={settings.facebookScanDelayMinMs}
              placeholder="default 10000"
              onChange={(v) => onChange({ ...settings, facebookScanDelayMinMs: v })}
            />
          </div>
          <div className="col-md-4">
            <NumberField
              field={{ key: 'facebookScanDelayMaxMs', label: 'Facebook max (ms)', hint: 'Default 60 000', min: 5_000, max: 300_000 }}
              value={settings.facebookScanDelayMaxMs}
              placeholder="default 60000"
              onChange={(v) => onChange({ ...settings, facebookScanDelayMaxMs: v })}
            />
          </div>
          <div className="col-md-4"></div>
          <div className="col-md-4">
            <NumberField
              field={{ key: 'websiteScanDelayMinMs', label: 'Website min (ms)', hint: 'Default 5 000', min: 1_000, max: 60_000 }}
              value={settings.websiteScanDelayMinMs}
              placeholder="default 5000"
              onChange={(v) => onChange({ ...settings, websiteScanDelayMinMs: v })}
            />
          </div>
          <div className="col-md-4">
            <NumberField
              field={{ key: 'websiteScanDelayMaxMs', label: 'Website max (ms)', hint: 'Default 30 000', min: 2_000, max: 120_000 }}
              value={settings.websiteScanDelayMaxMs}
              placeholder="default 30000"
              onChange={(v) => onChange({ ...settings, websiteScanDelayMaxMs: v })}
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function ExplainerCard({ s }: { s: CadenceSettings }) {
  const vBase = s.venueScanCooldownDays ?? 14;
  const vFav = s.favoriteVenueScanCooldownDays ?? 3;
  const vStale = s.staleVenueScanCooldownDays ?? 60;
  const vFavStale = s.staleFavoriteVenueScanCooldownDays ?? 21;
  const sBase = s.seriesScanCooldownDays ?? 14;
  const sFav = s.favoriteSeriesScanCooldownDays ?? 3;
  const sStale = s.staleSeriesScanCooldownDays ?? 60;
  const sFavStale = s.staleFavoriteSeriesScanCooldownDays ?? 21;
  const lookback = s.staleScanLookbackCount ?? 5;
  const maxAccepted = s.staleScanMaxNewPosts ?? 1;
  return (
    <section aria-labelledby="cadence-explainer-h" className="card mb-4">
      <div className="card-body">
        <h2 id="cadence-explainer-h" className="h5">
          How scan cadence works
        </h2>
        <p className="mb-2">
          Every venue and series with an Instagram handle, Facebook page, or website is auto-scanned on a rolling
          cooldown. Each (target, channel) gets its own next-eligible timestamp. The wait between scans is picked
          from a 2&times;2 matrix based on whether the target is favorited and whether its recent scans actually
          produced events that were <em>accepted</em>:
        </p>

        <div className="row g-3">
          <div className="col-md-6">
            <h3 className="h6 mb-1">Venues</h3>
            <div className="table-responsive">
              <table className="table table-sm align-middle mb-0" aria-label="Venue cooldown matrix">
                <thead>
                  <tr>
                    <th scope="col">&nbsp;</th>
                    <th scope="col">Active</th>
                    <th scope="col">Stale</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th scope="row">Not favorited</th>
                    <td>{vBase}d</td>
                    <td>{vStale}d</td>
                  </tr>
                  <tr>
                    <th scope="row">Favorited</th>
                    <td>{vFav}d</td>
                    <td>{vFavStale}d</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div className="col-md-6">
            <h3 className="h6 mb-1">Series</h3>
            <div className="table-responsive">
              <table className="table table-sm align-middle mb-0" aria-label="Series cooldown matrix">
                <thead>
                  <tr>
                    <th scope="col">&nbsp;</th>
                    <th scope="col">Active</th>
                    <th scope="col">Stale</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th scope="row">Not favorited</th>
                    <td>{sBase}d</td>
                    <td>{sStale}d</td>
                  </tr>
                  <tr>
                    <th scope="row">Favorited</th>
                    <td>{sFav}d</td>
                    <td>{sFavStale}d</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <p className="mt-3 mb-2">
          A channel (Instagram, Facebook, or Website) is marked <strong>stale</strong> when the total{' '}
          <em>accepted events</em> across its last <strong>{lookback}</strong> scans is{' '}
          <strong>{maxAccepted} or fewer</strong>. Counting accepted events instead of raw post counts means
          channels that flood us with content we always reject (happy hours, daily specials, generic flyers) are
          correctly classified as stale and moved to the wider cooldown — even if they post constantly.
        </p>
        <p className="mb-2 small text-body-secondary">What changes when you adjust each setting:</p>
        <ul className="small text-body-secondary mb-0">
          <li>
            <strong>Lower base cooldowns</strong> &rarr; more frequent scans &rarr; events appear sooner but cost
            more (Apify per-post + LLM extraction).
          </li>
          <li>
            <strong>Higher stale cooldowns</strong> &rarr; less spend on quiet handles, but you may miss the first
            few accepted events when a quiet target comes back.
          </li>
          <li>
            <strong>Larger lookback</strong> &rarr; more conservative stale verdicts (it takes longer for a channel
            to be flagged stale, and longer to recover when it wakes up).
          </li>
          <li>
            <strong>Higher max accepted events</strong> &rarr; more channels count as &ldquo;active&rdquo; and stay
            on the short cooldown.
          </li>
        </ul>
      </div>
    </section>
  );
}

export default function AdminScanCadencePage() {
  const [venues, setVenues] = useState<Venue[] | null>(null);
  const [series, setSeries] = useState<EventSeries[] | null>(null);
  const [lifetime, setLifetime] = useState<LifetimeResponse | null>(null);
  const [settings, setSettings] = useState<CadenceSettings | null>(null);
  const [savedSettings, setSavedSettings] = useState<CadenceSettings | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveErr, setSaveErr] = useState<string | null>(null);
  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);
  const [sortKey, setSortKey] = useState<SortKey>('staleCount');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  useEffect(() => {
    let cancelled = false;
    setLoadErr(null);
    Promise.all([
      api.get<Venue[]>('/api/mgmt/venues'),
      api.get<EventSeries[]>('/api/mgmt/series'),
      api.get<LifetimeResponse>('/api/mgmt/scans/lifetime'),
      api.get<CadenceSettings>('/api/mgmt/settings'),
    ])
      .then(([v, sList, l, st]) => {
        if (cancelled) return;
        setVenues(v);
        setSeries(sList);
        setLifetime(l);
        setSettings(st);
        setSavedSettings(st);
      })
      .catch((e) => {
        if (cancelled) return;
        setLoadErr(String(e?.message ?? e));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const lifetimeByKey = useMemo(() => {
    const m = new Map<string, LifetimeRow>();
    if (!lifetime) return m;
    for (const r of lifetime.items) {
      if (r.targetKind !== 'venue' && r.targetKind !== 'series') continue;
      m.set(`${r.targetKind}:${r.targetId}:${r.channel}`, r);
    }
    return m;
  }, [lifetime]);

  const allRows = useMemo<TargetRow[]>(() => {
    const out: TargetRow[] = [];
    if (venues) for (const v of venues) out.push(buildVenueRow(v, lifetimeByKey));
    if (series) for (const s of series) out.push(buildSeriesRow(s, lifetimeByKey));
    return out;
  }, [venues, series, lifetimeByKey]);

  const partitions = useMemo(() => {
    const set = new Set<string>();
    for (const r of allRows) {
      if (r.partitionKey) set.add(r.partitionKey);
    }
    return [...set].sort();
  }, [allRows]);

  const filteredRows = useMemo(() => {
    const filtered = applyFilters(allRows, filters);
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => compareRows(a, b, sortKey) * dir);
  }, [allRows, filters, sortKey, sortDir]);

  const totals = useMemo(() => {
    let stale = 0;
    let active = 0;
    let neverScanned = 0;
    for (const r of allRows) {
      if (!r.active) continue;
      if (r.configuredChannelCount === 0) continue;
      if (r.oldestScanMs === 0) neverScanned += 1;
      if (r.staleChannelCount > 0) stale += 1;
      else active += 1;
    }
    return { stale, active, neverScanned };
  }, [allRows]);

  const onSaveSettings = async () => {
    if (!settings) return;
    setSaving(true);
    setSaved(false);
    setSaveErr(null);
    try {
      // For per-channel override fields, when the user blanks a field that
      // was previously set we need to send `null` (explicit clear) instead
      // of omitting it — otherwise the server's patch-merge treats it as
      // "no change" and the old override sticks. JSON drops `undefined`
      // properties; `null` survives.
      const payload: Record<string, unknown> = { ...settings };
      if (savedSettings) {
        for (const k of CHANNEL_OVERRIDE_KEYS) {
          if (settings[k] === undefined && savedSettings[k] !== undefined) {
            payload[k] = null;
          }
        }
      }
      await api.patch('/api/mgmt/settings', payload);
      setSavedSettings(settings);
      setSaved(true);
      window.setTimeout(() => setSaved(false), 3000);
    } catch (e: any) {
      setSaveErr(String(e?.message ?? e));
    } finally {
      setSaving(false);
    }
  };

  const dirty = useMemo(() => {
    if (!settings || !savedSettings) return false;
    const allKeys = [
      ...VENUE_FIELDS.map((f) => f.key),
      ...SERIES_FIELDS.map((f) => f.key),
      ...SHARED_FIELDS.map((f) => f.key),
      ...CHANNEL_OVERRIDE_KEYS,
      'redditScanCooldownDays' as NumericSettingKey,
      'instagramScanDelayMinMs' as NumericSettingKey,
      'instagramScanDelayMaxMs' as NumericSettingKey,
      'facebookScanDelayMinMs' as NumericSettingKey,
      'facebookScanDelayMaxMs' as NumericSettingKey,
      'websiteScanDelayMinMs' as NumericSettingKey,
      'websiteScanDelayMaxMs' as NumericSettingKey,
    ];
    if (allKeys.some((k) => settings[k] !== savedSettings[k])) return true;
    if ((settings.facebookScanNonFavoritedEnabled === true) !== (savedSettings.facebookScanNonFavoritedEnabled === true)) return true;
    if ((settings.useSociavaultForInstagram === true) !== (savedSettings.useSociavaultForInstagram === true)) return true;
    if ((settings.useSociavaultForReddit === true) !== (savedSettings.useSociavaultForReddit === true)) return true;
    if ((settings.redditPollEnabled !== false) !== (savedSettings.redditPollEnabled !== false)) return true;
    return false;
  }, [settings, savedSettings]);

  function header(label: string, key: SortKey) {
    const active = sortKey === key;
    const arrow = active ? (sortDir === 'asc' ? '↑' : '↓') : '';
    return (
      <button
        type="button"
        className="btn btn-link btn-sm p-0 text-decoration-none"
        onClick={() => {
          if (active) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
          else {
            setSortKey(key);
            setSortDir(key === 'name' || key === 'partition' || key === 'kind' ? 'asc' : 'desc');
          }
        }}
        aria-label={`Sort by ${label}`}
      >
        {label} {arrow}
      </button>
    );
  }

  return (
    <AdminLayout>
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <h1 className="mb-0">Scan cadence</h1>
      </div>
      <p className="text-body-secondary small">
        Tune how often venues and series are auto-scanned, see which channels the engine is currently treating as
        stale, and spot-check coverage across both.
      </p>

      {loadErr ? (
        <div className="alert alert-danger" role="alert">
          {loadErr}
        </div>
      ) : null}

      {!venues || !series || !lifetime || !settings ? (
        <LoadingBlock label="Loading scan cadence dashboard" />
      ) : (
        <>
          <ExplainerCard s={savedSettings ?? settings} />

          <SettingsCard
            settings={settings}
            onChange={setSettings}
            onSave={onSaveSettings}
            saving={saving}
            saved={saved && !dirty}
            error={saveErr}
          />

          <section aria-labelledby="cadence-targets-h">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
              <h2 id="cadence-targets-h" className="h5 mb-0">
                Targets ({filteredRows.length} of {allRows.length})
              </h2>
              <div className="small text-body-secondary">
                {totals.active} fully active &middot; {totals.stale} have a stale channel &middot;{' '}
                {totals.neverScanned} never scanned. Lifetime window: last {lifetime.retentionDays} days.
              </div>
            </div>

            <div className="card mb-3">
              <div className="card-body py-2">
                <div className="row g-2 align-items-end">
                  <div className="col-md-3">
                    <label htmlFor="cad-search" className="form-label small mb-1">
                      Search by name
                    </label>
                    <input
                      id="cad-search"
                      type="search"
                      className="form-control form-control-sm"
                      value={filters.search}
                      onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    />
                  </div>
                  <div className="col-md-2">
                    <label htmlFor="cad-kind" className="form-label small mb-1">
                      Kind
                    </label>
                    <select
                      id="cad-kind"
                      className="form-select form-select-sm"
                      value={filters.kind}
                      onChange={(e) => setFilters({ ...filters, kind: e.target.value as KindFilter })}
                    >
                      <option value="all">All</option>
                      <option value="venue">Venues</option>
                      <option value="series">Series</option>
                    </select>
                  </div>
                  <div className="col-md-2">
                    <label htmlFor="cad-part" className="form-label small mb-1">
                      Group
                    </label>
                    <select
                      id="cad-part"
                      className="form-select form-select-sm"
                      value={filters.partition}
                      onChange={(e) => setFilters({ ...filters, partition: e.target.value })}
                    >
                      <option value="">All</option>
                      {partitions.map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-2">
                    <label htmlFor="cad-stale" className="form-label small mb-1">
                      Staleness
                    </label>
                    <select
                      id="cad-stale"
                      className="form-select form-select-sm"
                      value={filters.staleness}
                      onChange={(e) => setFilters({ ...filters, staleness: e.target.value as StalenessFilter })}
                    >
                      <option value="any">Any</option>
                      <option value="any-stale">Any channel stale</option>
                      <option value="all-stale">All channels stale</option>
                      <option value="all-active">All channels active</option>
                      <option value="never-scanned">Never scanned</option>
                    </select>
                  </div>
                  <div className="col-md-2">
                    <label htmlFor="cad-channel" className="form-label small mb-1">
                      Channel
                    </label>
                    <select
                      id="cad-channel"
                      className="form-select form-select-sm"
                      value={filters.channel}
                      onChange={(e) => setFilters({ ...filters, channel: e.target.value as ChannelFilter })}
                    >
                      <option value="any">Any</option>
                      <option value="instagram">Has Instagram</option>
                      <option value="facebook">Has Facebook</option>
                      <option value="website">Has Website</option>
                      <option value="missing-any">Missing at least one</option>
                    </select>
                  </div>
                  <div className="col-md-1">
                    <div className="form-check mt-3">
                      <input
                        id="cad-active"
                        type="checkbox"
                        className="form-check-input"
                        checked={filters.activeOnly}
                        onChange={(e) => setFilters({ ...filters, activeOnly: e.target.checked })}
                      />
                      <label htmlFor="cad-active" className="form-check-label small">
                        Active only
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="table-responsive">
              <table className="table table-sm align-top">
                <thead>
                  <tr>
                    <th scope="col">{header('Target', 'name')}</th>
                    <th scope="col">{header('Kind', 'kind')}</th>
                    <th scope="col">{header('Group', 'partition')}</th>
                    <th scope="col">Instagram</th>
                    <th scope="col">Facebook</th>
                    <th scope="col">Website</th>
                    <th scope="col">{header('Stale', 'staleCount')}</th>
                    <th scope="col">{header('Accepted', 'accepted')}</th>
                    <th scope="col">{header('New posts', 'totalNew')}</th>
                    <th scope="col">{header('Oldest scan', 'oldestScan')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((r) => (
                    <tr key={`${r.kind}:${r.id}`}>
                      <th scope="row" className="fw-normal">
                        <Link to={r.detailUrl} className="text-decoration-none">
                          {r.name}
                        </Link>
                        {!r.active ? <span className="badge bg-secondary ms-1">inactive</span> : null}
                      </th>
                      <td className="small text-body-secondary">{r.kind}</td>
                      <td className="small text-body-secondary">{r.partitionKey || '—'}</td>
                      <td>
                        <ChannelCell stat={r.channels.instagram} label="Instagram" />
                      </td>
                      <td>
                        <ChannelCell stat={r.channels.facebook} label="Facebook" />
                      </td>
                      <td>
                        <ChannelCell stat={r.channels.website} label="Website" />
                      </td>
                      <td className="small">
                        {r.staleChannelCount}/{r.configuredChannelCount}
                      </td>
                      <td className="small">{r.totalEventsAccepted}</td>
                      <td className="small">{r.totalPostsNew}</td>
                      <td className="small text-body-secondary">
                        {r.oldestScanMs > 0 ? relativeTime(new Date(r.oldestScanMs).toISOString()) : 'never'}
                      </td>
                    </tr>
                  ))}
                  {filteredRows.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="text-center text-body-secondary py-4">
                        No targets match the current filters.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </AdminLayout>
  );
}
